import { createRoot } from 'react-dom/client';
import { useState, useRef } from 'react';
import { getCsrfToken } from '../utils/getCsrfToken.js';
import { CheckBoxGroup, FileField, SelectGroup, TextField } from '../common/components/formElements.js';

const IntroSection = () => {
    return (
        <div className='container'>
            <h2>No current openings that interest you? Submit your profile here.</h2>
            <p>Our team is growing! If you&lsquo;re passionate about our mission and want to make a difference in classrooms
                across America everyday, we&lsquo;d like to hear from you.</p>
            <p>Please submit your resume and tell us a bit about your experience and interests. We&lsquo;ll review your
                materials and get in touch when we post new roles that fit your profile.</p>
            <p>Thanks for your interest in DonorsChoose!</p>
            <h2><strong>Hybrid Workplace and Other Details</strong></h2>
            <p>At DonorsChoose, you&lsquo;ll have the option to work a flexible hybrid schedule in our NYC office, or to work
                fully remotely from CA, CO, CT, DC, FL, GA, IL, IN, ME, MA, MD, MI, NJ, NY, OR, PA, RI, TX, VA, WA,
                WI.</p>
            <p>Candidates who are not in the NYC area should expect to travel to our NYC office on an as-needed basis,
                about 4-6 times per year. All work-related travel expenses will be covered by the organization and
                proper safety precautions will be taken to prevent the spread of COVID-19.</p>
            <p>DonorsChoose is a fully vaccinated workplace and all new employees, regardless of their work location,
                will be asked to share proof of full vaccination against COVID-19 within two weeks of their hire date or
                request an exemption from this policy due to a disability or sincerely held religious belief or
                practice. This policy does not apply to employees in states with state restrictions on vaccine
                mandates.</p>
            <p>DonorsChoose participates in E-Verify. We will provide the U.S. Social Security Administration (SSA) and,
                if necessary, the U.S. Department of Homeland Security (DHS) with information from each new employee&lsquo;s
                Form I-9 to confirm work authorization</p>
            <h4><strong><em>A Final Note</em></strong><br/></h4>
            <p style={{ fontWeight: 400 }}>
                <em>
                    <p>The DonorsChoose team works toward a nation where students in every community have the resources needed for an excellent education. To do this we hire and support a diverse team of the best and the brightest talent available.</p>
                    <p>We are an organization increasingly representative of the varied races and ethnicities, genders and sexual orientations, religious and political beliefs, and abilities that comprise our nation. DonorsChoose.org focuses on attracting, retaining, and advancing diverse talent because it makes us more effective, high-performing, creative, and resilient.</p>
                    <p>If you are passionate about our mission, highly skilled in your field, and looking for a place where you can bring all of yourself to work, we want you.</p>
                </em>
            </p>
        </div>
    );
};

const ProspectCareerPage = () => {
    const url = secureBaseURL + 'careers?prospect';
    const [busy, setBusy] = useState(false);
    const [showForm, setShowForm] = useState(true);

    const formRefs = {
        first_name: useRef(),
        last_name: useRef(),
        email: useRef(),
        phone: useRef(),
        resume: useRef(),
        role: useRef(),
        team: useRef(),
        pronouns: useRef(),
        visa: useRef(),
        reference: useRef(),
        linkedin: useRef(),
        website: useRef(),
        race: useRef(),
        gender: useRef(),
        first_gen_college: useRef(),
    };

    const validateForm = async function (event) {
        event.preventDefault();
        setBusy(true);

        const invalidField = Object.keys(formRefs).filter((ref) => !formRefs[ref].current.validate());

        const formData = new FormData(event.target);

        if (invalidField.length > 0) {
            setBusy(false);
            formRefs[invalidField[0]].current.scrollIntoView();
            return;
        }

        fetch(url, {
            method: 'POST',
            cache: 'no-cache',
            headers: {
                // csrfTokenHeaderName defined in globalJavascriptVariables.tag
                // eslint-disable-next-line no-undef
                [csrfTokenHeaderName]: getCsrfToken(),
            },
            credentials: 'include',
            body: formData,
        }).then((response) => {
            if (response.ok) {
                setShowForm(false);
            } else {
                alert('Sorry, we has an error recording your resume. Please try again later.');
            }
        });
    };

    return (
        <form className='form-redux' onSubmit={validateForm}>
            <IntroSection />
            {!showForm &&
                <div className='prospect-form container' style={{ backgroundColor: '#FAFAFA' }}>
                    <section>Thank you for submitting your resume!</section>
                </div>
            }
            {showForm &&
                <div className='prospect-form container' style={{ backgroundColor: '#FAFAFA' }}>
                    <h3>Apply for this Job</h3> <div><span className="asterisk">&nbsp;*</span> Required</div>
                    <TextField name='firstName' label='First Name' isRequired={true} ref={formRefs.first_name}/>
                    <TextField name='lastName' label='Last Name' isRequired={true} ref={formRefs.last_name}/>
                    <TextField name='email' label='Email' isRequired={true} ref={formRefs.email}/>
                    <TextField name='phone' label='Phone' ref={formRefs.phone}/>
                    <FileField name='resume' label='Resume/CV' isRequired={true} ref={formRefs.resume}
                        accepts="application/msword,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/html,text/plain" />
                    <hr/>

                    <div id="custom_fields">
                        <CheckBoxGroup name='role' isRequired={true} ref={formRefs.role} boxes={[
                            { label: 'Full-Time', value: 'fullTime' },
                            { label: 'Part-Time', value: 'partTime' },
                            { label: 'Fellowship', value: 'fellowship' },
                            { label: 'Internship', value: 'internship' },
                            { label: 'Volunteer', value: 'volunteer' },
                        ]} label='What type of work are you looking for? Choose all that apply.' />

                        <CheckBoxGroup name='team' isRequired={true} ref={formRefs.team} boxes={[
                            { label: 'Advocacy & Public Partnerships', value: 'advocacy-partnerships' },
                            { label: 'Finance, IT, and Procurement', value: 'finance-fulfillment' },
                            { label: 'Marketing & Partnerships', value: 'marketing-partnerships' },
                            { label: 'Operations', value: 'operations' },
                            { label: 'People Operations', value: 'people-operations' },
                            { label: 'Product, Engineering & Data', value: 'prod-eng-data' },
                            { label: 'Equity & Impact', value: 'equity-and-impact' },
                            { label: 'Strategy & Innovation', value: 'strategy-and-innovation' },
                        ]} label='Which team(s) are you most interested in joining?'/>

                        <SelectGroup name='pronouns' isRequired={true} label='Pronouns' ref={formRefs.pronouns} options={[
                            { label: 'Please select', value: '' },
                            { label: 'She/Her/Hers', value: 'she' },
                            { label: 'He/Him/His', value: 'he' },
                            { label: 'They/Them/Theirs', value: 'they' },
                            { label: 'Ze/Hir/Hirs', value: 'Ze' },
                            { label: 'Other', value: 'other' },
                        ]} />
                        <SelectGroup name='visa' isRequired={true} ref={formRefs.visa} options={[
                            { label: 'Please select', value: '' },
                            { label: 'Yes', value: 'yes' },
                            { label: 'No', value: 'no' },
                        ]} label='Will you require Visa sponsorship, now or in the future, for employment in the United States?' />
                        <TextField name='reference' label='Who referred you to our organization? Enter their first and last name.' ref={formRefs.reference}/>
                        <TextField name='linkedin' label='LinkedIn Profile' ref={formRefs.linkedin}/>
                        <TextField name='website' label='Website' ref={formRefs.website}/>
                    </div>
                    <div id="custom_fields">
                        <hr/>
                        <div>
                            <h4>Demographic Questions</h4>
                            <p>
                                At DonorsChoose.org, we are committed to seeking equity throughout the nation&lsquo;s classrooms and within our organization. We celebrate diversity and are committed to building a team that reflects the students and teachers we serve. We welcome and encourage qualified applicants from all backgrounds who are excited to contribute to our mission.
                            </p>
                            <p>
                                We do not discriminate on the basis of race, religion, color, national origin, age, gender identity and expression, sexual orientation, marital status, veteran status, or disability.
                            </p>
                            <p>
                                We invite you to voluntarily provide demographic information that will help inform and improve our inclusion efforts and strengthen our hiring process. Demographic information will not be associated with your individual application, and has no effect on your opportunity for employment.
                            </p>

                            <CheckBoxGroup name='race' ref={formRefs.race} boxes={[
                                { label: 'Indigenous / Native American', value: 'native' },
                                { label: 'Black / African American', value: 'black' },
                                { label: 'East Asian', value: 'east-asian' },
                                { label: 'Latinx / Hispanic', value: 'latinx' },
                                { label: 'Middle Eastern / North African', value: 'middle-eastern' },
                                { label: 'Pacific Islander', value: 'pacific-islander' },
                                { label: 'South Asian', value: 'south-asian' },
                                { label: 'Southeast Asian', value: 'southeast-asian' },
                                { label: 'White', value: 'white' },
                                { label: 'I prefer to self-describe', value: 'self-describe' },
                                { label: 'I prefer not to say', value: 'prefer-not' },
                            ]} label='Which of the following best represents your racial or ethnic heritage? Choose all that apply.' />

                            <SelectGroup name='gender' ref={formRefs.gender} options={[
                                { label: 'Please select', value: '' },
                                { label: 'Female', value: 'female' },
                                { label: 'Male', value: 'male' },
                                { label: 'Non-binary / third gender', value: 'non-binary' },
                                { label: 'I prefer to self describe', value: 'self-describe' },
                                { label: 'I prefer not to say', value: 'prefer-not' },
                            ]} label='Which of the following best represents your gender identity?' />

                            <SelectGroup name='firstGenCollege' ref={formRefs.first_gen_college} options={[
                                { label: 'I prefer not to say', value: '' },
                                { label: 'Yes', value: 'yes' },
                                { label: 'No', value: 'no' },
                            ]} label='Are you a first-generation college student (i.e. neither of your parent(s) or guardian(s) graduated from a 4-year college)?' />
                        </div>
                    </div>
                    <div className="input-holder submit-holder">
                        <div className="inline-button">
                            <input className="button-primary small" type="submit" id="sendButton" value="Submit Application" disabled={busy}/>
                        </div>
                    </div>
                </div>
            }
        </form>
    );
};

(function () {
    const root = createRoot(document.getElementById('career-container'));
    root.render(<ProspectCareerPage />);
})();
